import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Upload, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { cdnUrl } from '@/lib/config/site'

// Pre-generated avatars
const preGeneratedAvatars = [
  { id: '1', name: 'Marcus', imageUrl: cdnUrl('/avatars/marcus.jpg') },
  { id: '2', name: 'James', imageUrl: cdnUrl('/avatars/james.jpg') },
  { id: '3', name: 'David', imageUrl: cdnUrl('/avatars/david.jpg') },
  { id: '4', name: 'Michael', imageUrl: cdnUrl('/avatars/michael.jpg') },
  { id: '5', name: 'Olivia', imageUrl: cdnUrl('/avatars/olivia.jpg') },
  { id: '6', name: 'Emma', imageUrl: cdnUrl('/avatars/emma.jpg') },
  { id: '7', name: 'Sophia', imageUrl: cdnUrl('/avatars/sophia.jpg') },
  { id: '8', name: 'Sarah', imageUrl: cdnUrl('/avatars/sarah.jpg') },
]

export default function AvatarsPage() {
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleUpload = () => {
    // TODO: Implement file upload
    setIsUploading(true)
    setTimeout(() => setIsUploading(false), 1500)
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>Avatars - Footage</title>
        <meta name="description" content="Manage your AI avatars." />
      </Helmet>
      <div className="max-w-4xl">
        <h1 className="text-2xl font-bold text-neutral-900">Avatars</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Choose an avatar for your video characters or upload your own.
        </p>

        {/* Upload Section */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-neutral-700 mb-3">Upload Custom Avatar</h2>
          <div className="border-2 border-dashed border-neutral-200 rounded-xl p-8 text-center hover:border-neutral-300 transition-colors">
            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
              <Upload className="h-5 w-5 text-neutral-400" />
            </div>
            <p className="text-sm text-neutral-600 mb-1">Drag and drop an image here</p>
            <p className="text-xs text-neutral-400 mb-4">PNG, JPG up to 10MB</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Browse Files'
              )}
            </Button>
          </div>
        </div>

        {/* Pre-generated Avatars */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold text-neutral-700 mb-3">Pre-generated Avatars</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {preGeneratedAvatars.map((avatar) => (
              <button
                key={avatar.id}
                onClick={() => setSelectedAvatar(avatar.id)}
                className={cn(
                  'relative aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105',
                  selectedAvatar === avatar.id
                    ? 'border-teal-500 ring-2 ring-teal-500/20'
                    : 'border-neutral-200 hover:border-neutral-300'
                )}
              >
                <img
                  src={avatar.imageUrl}
                  alt={avatar.name}
                  className="w-full h-full object-cover bg-neutral-100"
                />
                {selectedAvatar === avatar.id && (
                  <div className="absolute inset-0 bg-teal-500/10 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/50 to-transparent p-1.5">
                  <p className="text-xs text-white font-medium truncate">{avatar.name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Avatar Action */}
        {selectedAvatar && (
          <div className="mt-6 flex items-center gap-3">
            <Button className="bg-teal-600 hover:bg-teal-700">
              Use Selected Avatar
            </Button>
            <Button variant="ghost" onClick={() => setSelectedAvatar(null)}>
              Clear Selection
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
